import type { LocalTimeParts } from './types';

function partValue(parts: Intl.DateTimeFormatPart[], type: string): string {
  return parts.find((part) => part.type === type)?.value ?? '';
}

function parseOffsetMinutes(value: string): number {
  const normalized = value.replace('GMT', '').trim();
  if (!normalized || normalized === 'UTC') return 0;
  const match = normalized.match(/^([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!match) return 0;
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0));
}

export function browserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function localTimeParts(now: Date, timezone: string): LocalTimeParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    weekday: 'short',
    timeZoneName: 'shortOffset',
  }).formatToParts(now);

  const year = partValue(parts, 'year');
  const month = partValue(parts, 'month');
  const day = partValue(parts, 'day');
  const hour = Number(partValue(parts, 'hour') || 0);
  const minute = partValue(parts, 'minute');
  const second = partValue(parts, 'second');
  const offsetMinutes = parseOffsetMinutes(partValue(parts, 'timeZoneName'));
  const offsetSign = offsetMinutes < 0 ? '-' : '+';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const offsetMins = String(absOffset % 60).padStart(2, '0');
  const localDate = `${year}-${month}-${day}`;

  return {
    occurred_at_utc: now.toISOString(),
    restaurant_timezone: timezone,
    occurred_at_local: `${localDate}T${String(hour).padStart(2, '0')}:${minute}:${second}${offsetSign}${offsetHours}:${offsetMins}`,
    local_date: localDate,
    local_hour: hour,
    local_day_of_week: weekdayNumber(now, timezone),
    utc_offset_minutes: offsetMinutes,
    browser_timezone: browserTimezone(),
  };
}

function weekdayNumber(now: Date, timezone: string): number {
  const value = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' }).format(now);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(value);
}
